import { type FC, memo, useRef, useState, useEffect, useCallback, type RefObject, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useCampaignTypeContext, useCampaignsStore, useSelectedCampaign } from 'features/campaigns';
import {
  ZERO_TIME,
  createCountdownResult,
  type CountdownDirection,
  type CountdownResult,
} from '../../countdownHelpers';

/**
 * Calculates countdown for campaigns.
 * Simple startDate → endDate lifecycle with no interval slots.
 */
const calculateCountdown = (
  startDate: string | undefined,
  endDate: string | undefined,
  t: (key: string, fallback: string) => string,
): CountdownResult => {
  if (!startDate || !endDate) {
    return { label: t('campaigns.starts_in', 'Starts In'), value: ZERO_TIME, direction: 'startsIn', isTicking: true };
  }

  const now = new Date();
  const start = new Date(startDate);
  const end = new Date(endDate);

  if (now < start) {
    return createCountdownResult(now, start, 'startsIn', t);
  }

  if (now <= end) {
    return createCountdownResult(now, end, 'endsIn', t);
  }

  return {
    label: t('campaigns.status', 'Status'),
    value: t('campaigns.finished', 'Finished'),
    direction: 'endsIn',
    isTicking: false,
  };
};

const useCampaignForTimer = (campaignId?: string) => {
  const campaigns = useCampaignsStore((state) => state.campaigns);

  const campaignType = useCampaignTypeContext();

  const selectedCampaign = useSelectedCampaign(campaignType);

  // For 'panel' or no campaignId, use the selected campaign dates
  const campaign = campaignId && campaignId !== 'panel'
    ? campaigns.find(c => c.id === campaignId)
    : selectedCampaign;

  return { startDate: campaign?.startDate, endDate: campaign?.endDate };
};

interface CountdownTimerProps {
  campaignId?: string;
  children: (props: {
    label: string;
    valueRef: RefObject<HTMLSpanElement | null>;
    direction: CountdownDirection;
    initialValue: string;
    lessThanHour: boolean;
  }) => ReactNode;
}

// Calculates is there less than an hour on the timer
// regardless of the campaign state (campaign not started yet or active one)
const getLessThanHour = (startDate?: string, endDate?: string): boolean => {
  if (!startDate || !endDate) return false;
  const now = new Date();
  const start = new Date(startDate);
  const end = new Date(endDate);
  const target = now < start ? start : end;
  const diff = target.getTime() - now.getTime();
  return diff > 0 && diff < 3600 * 1000;
};

/**
 * Simple countdown timer — simple startDate/endDate countdown with no interval slots.
 * Uses the same Starts In/On, Ends In/On display as the Interval timer.
 * DOM-direct ticking via ref for performance.
 */
const SimpleCountdownTimer: FC<CountdownTimerProps> = ({ campaignId, children }) => {
  const { t } = useTranslation();
  const { startDate, endDate } = useCampaignForTimer(campaignId);

  const valueRef = useRef<HTMLSpanElement>(null);
  const prevLabelRef = useRef('');
  const prevDirectionRef = useRef<CountdownDirection>('startsIn');
  const prevLessThanHourRef = useRef(false);

  const getResult = useCallback(
    () => calculateCountdown(startDate, endDate, t),
    [startDate, endDate, t],
  );

  const [state, setState] = useState(() => {
    const result = getResult();
    return { label: result.label, direction: result.direction, isTicking: result.isTicking, lessThanHour: getLessThanHour(startDate, endDate) };
  });

  useEffect(() => {
    const tick = () => {
      const result = getResult();
      const lessThanHour = getLessThanHour(startDate, endDate);

      if (valueRef.current) {
        valueRef.current.textContent = result.value;
      }

      if (
        prevLabelRef.current !== result.label ||
        prevDirectionRef.current !== result.direction ||
        prevLessThanHourRef.current !== lessThanHour
      ) {
        prevLabelRef.current = result.label;
        prevDirectionRef.current = result.direction;
        prevLessThanHourRef.current = lessThanHour;
        setState({ label: result.label, direction: result.direction, isTicking: result.isTicking, lessThanHour: lessThanHour });
      }
    };

    tick();

    if (!state.isTicking) return;

    const intervalId = setInterval(tick, 1000);
    return () => clearInterval(intervalId);
  }, [getResult, startDate, endDate, state.isTicking]);

  const initialValue = getResult().value;

  return <>{children({ label: state.label, valueRef, direction: state.direction, initialValue, lessThanHour: state.lessThanHour })}</>;
};

export default memo(SimpleCountdownTimer);
